<template>
  <div class="client-action-add flex-grow-1 d-flex flex-column">
    <b-container class="flex-grow-1 d-flex flex-column">
      <b-row class="flex-grow-1 d-flex flex-column">
        <b-col class="flex-grow-1 d-flex flex-column">
          <div class="floating-panel flex-grow-1 d-flex flex-column">
            <b-progress
              :value="progress"
              height="4px"
              variant="primary"
            ></b-progress>
            <vue-element-loading :active="loading"></vue-element-loading>
            <r-form
              :disabled="false"
              :validators="validators[1]"
              @submit="onStepOne"
              key="one"
              title="Select Property"
              v-model="forms[1]"
              v-show="step === 1"
            >
              <b-alert
                      show
                      v-if="clientActionNotAvailable"
                      variant="danger"
                    >
                Client action could not be added at this time.
              </b-alert>
              <b-alert
                      show
                      v-if="noPropertiesFound"
                      variant="danger"
              >
                No properties found
              </b-alert>

              <template
                      slot="buttons"
                      slot-scope="{ form, valid }"
              >
              <b-button
                      :to="{name: 'clientActions'}"
                      variant="outline-secondary"
              >
                Cancel
              </b-button>
              <b-button
                      :disabled="valid"
                      @click="onStepOne(form)"
                      class="btn-arrow"
                      variant="primary"
              >
                Next
                <font-awesome-icon :icon="['far', 'arrow-right']"></font-awesome-icon>
              </b-button>
              </template>
            </r-form>

            <r-form
                    :disabled="!property"
                    :validators="validators[2]"
                    :uploadFiles="uploadFiles"
                    @submit="onStepTwo"
                    @onFileSuccess="onFileUploadSuccess"
                    @onFileError="onFileUploadError"
                    @noFilesSelected="addClientAction"
                    key="two"
                    title="Action details"
                    v-model="forms[2]"
                    v-show="step === 2"
            >
              <b-alert
                      show
                      v-if="!property"
                      variant="danger"
              >
                Selected property could not be found
              </b-alert>
              <template
                      slot="buttons"
                      slot-scope="{ form, valid }"
              >
                <b-button
                        @click="step = 1"
                        variant="outline-secondary"
                >
                  Back
                </b-button>
                <b-button
                        :disabled="valid"
                        @click="onStepTwo"
                        class="btn-arrow"
                        variant="primary"
                >
                  Create Client Action
                  <font-awesome-icon :icon="['far', 'arrow-right']"></font-awesome-icon>
                </b-button>
              </template>
            </r-form>


            <div v-show="step === 3">
              <div class="form-body text-center p-5">
                <h2 class="title success green">
                  Client Action successfully added
                </h2>
                <font-awesome-icon
                        :icon="['fal', 'check']"
                        class="green h1"
                        size="lg"
                ></font-awesome-icon>
                <div
                        :class="[
                          'mt-4',
                          'ml-auto',
                          'mr-auto',
                          'text-center',
                        ]"
                >
                  <b-button
                          :to="{ name: 'clientActions' }"
                          variant="primary"
                  >View Client Actions</b-button>
                </div>
              </div>
            </div>
          </div>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import ClientAction from '@/services/Action/ClientAction';
import { forEach } from 'lodash';
import { required } from 'vuelidate/lib/validators';
import { form as ClientActionForm, validators as ClientActionValidators } from '@/services/Action/ClientActionForm';
import Property from '@/services/Property/Property';
import vueDropzone from 'vue2-dropzone';

export default {
  name: 'Client-Actions-Add',
  components: {
    Property,
    vueDropzone,
  },
  data() {
    return {
      forms: {
        1: {
          propertyId: {
            hidden: function() {return this.options.length === 0;},
            label: 'Select Property',
            type: 'select',
            value: '',
            trackBy: 'propertyId',
            labelBy: 'label',
            options: [],
          },
        },
        2: ClientActionForm(),
      },
      validators: {
        1: {
          propertyId: {
            required,
          },
        },
        2: ClientActionValidators,
      },
      step: 1,
      loading: false,
      clientActionNotAvailable: false,
      noPropertiesFound: false,
      uploadFiles: false,
    };
  },
  computed: {
    progress() {
      return (100 / 3) * this.step;
    },
    property() {
      return this.forms[1].propertyId.value;
    },
  },
  created() {
    this.searchProperties();
  },
  watch: {
    // Reset
    step(newStep) {
      if (newStep === 1) {
        this.resetForm(2);
      }
    },
  },
  methods: {
    async onStepOne(fields) {
      this.loading = true;

      if (!this.property) {
        return false;
      }

      this.loadPropertyActions();

      const form = this.forms[2];
      this.step = 2;
      this.loading = false;

      return true;
    },
    async onStepTwo() {
      this.loading = true;

      this.performUpload();
    },

    async performUpload() {
      this.forms[2].propertyId.value = this.property.propertyId;
      this.uploadFiles = true;
    },

    async addClientAction() {
      if (!this.loading) {
        return;
      }

      const form = this.forms[2];

      const params = {};
      forEach(form, (object, field) => {
        params[field] = object.value;
      });

      params.linkedActionId = (params.linkedActionId) ? params.linkedActionId.clientActionId : null;
      params.priorityId = params.priorityId.priorityId;
      params.propertyId = this.property.propertyId;

      try {
        await ClientAction.add(params);

        this.step = 3;
        this.resetForm(1);
        this.resetForm(2);
      } finally {
        this.loading = false;
      }
    },

    resetForm(index) {
      forEach(this.forms[index], (object, key) => {
        if (key in this.forms[index]) {
          this.$set(this.forms[index][key], 'value', '');
        }
      });
    },

    async searchProperties() {
      this.noPropertiesFound = false;
      this.forms['1'].propertyId.value = '';

      try {
        const params = {};

        const { data } = await Property.list({params});

        const results = [];
        forEach(data.data, (item, key) => {
          results.push({
            ...item,
            label: this.formatAddress(item),
          });
        });

        this.forms['1'].propertyId.options = results;

        if (results.length === 0) {
          this.noPropertiesFound = true;
        }

      } catch (e) {

      }
    },
    formatAddress(data) {
      const validatedStreetNumber = data.streetNumber || '';
      const validatedStreet = data.street || '';

      return data.uprn + ' - ' + [
        data.numberRange,
        data.buildingName,
        `${validatedStreetNumber} ${validatedStreet}`,
        data.town,
        data.postcode,
      ].filter(Boolean).join(', ');
    },
    async loadPropertyActions() {
      const { data } = await Property.getActions(this.property.propertyId);

      const results = [];
      forEach(data.data, (item, key) => {
        results.push({
          ...item,
          label: item.clientActionId + ' - ' + item.recommendation,
        });
      });

      this.forms[2].linkedActionId.options = results;
      if (results.length > 0) {
        this.forms[2].linkedActionId.disabled = false;
      } else {
        this.forms[2].linkedActionId.disabled = true;
      }
    },
    onFileUploadSuccess() {
      this.uploadFiles = false;
      this.step = 3;
      this.resetForm(1);
      this.resetForm(2);
      this.loading = false;
    },
    onFileUploadError() {
      this.uploadFiles = false;
      this.loading = false;
    },
  },
};
</script>

<style lang="scss">
  .clientActionsAdd {
    background: $gray;

    .client-action-add {
      margin-top: 40px;
    }

    .floating-panel {
      box-shadow: -4px 5px 6px -3px rgba(0, 0, 0, 0.16);
      border-radius: 3px;
      background: white;
      margin-bottom: 100px;

      .multiselect__tags {
        padding-right: 50px;
      }
      .multiselect__content-wrapper {
        .multiselect__element {
          span {
            white-space: normal;
          }
        }
      }
    }
    .vue-dropzone {
      .dz-preview {
        .dz-remove {
          z-index: auto;
        }
        .dz-details {
          z-index: auto;
        }
      }
    }
  }

</style>
